/**
 * Analytics Service — Minimal SaaS-Standard Event Tracking
 *
 * Tracks only essential product events that drive business decisions:
 *   - Session lifecycle (start/end)
 *   - Page views
 *   - Core feature usage (file ops, AI usage, collaboration, export)
 *   - Errors
 *
 * Events are queued in IndexedDB, batched, and synced to the server.
 * Offline-first: events persist locally and sync when back online.
 */

import API_BASE_URL from '../config/api';
import screenwriterDB, { STORES } from '../storage/indexedDB';
import { isElectron } from '../utils/electronBridge';
import { hasGivenConsent } from '../components/common/CookieConsent';

const PLATFORM = isElectron ? 'desktop' : 'web';

const BATCH_SIZE = 50;
const FLUSH_INTERVAL = 60000; // flush every 60s (not 30s — less aggressive)
const MAX_QUEUE_SIZE = BATCH_SIZE * 20; // hard ceiling — events beyond this are dropped, not retried forever

class AnalyticsService {
  constructor() {
    this.sessionId = crypto.randomUUID();
    this.userId = null;
    this.isOnline = navigator.onLine;
    this.flushTimer = null;
    this.isSyncing = false;
    this.eventQueue = [];
    this.sessionStartTime = Date.now();

    // Expose instance globally for coordination with syncService
    window.__analyticsService = this;

    window.addEventListener('online', () => { this.isOnline = true; this.flush(); });
    window.addEventListener('offline', () => { this.isOnline = false; });
    window.addEventListener('beforeunload', () => this.flushSync());

    this.startPeriodicFlush();
    this.rehydrateQueue(); // Load any un-synced events from IndexedDB
  }

  /** Push events onto the queue, dropping the oldest entries past MAX_QUEUE_SIZE
   * instead of growing unbounded (e.g. while offline or the endpoint is down). */
  enqueue(...items) {
    this.eventQueue.push(...items);
    if (this.eventQueue.length > MAX_QUEUE_SIZE) {
      this.eventQueue.splice(0, this.eventQueue.length - MAX_QUEUE_SIZE);
    }
  }

  /** Put a failed batch back at the front for retry priority, trimming from
   * the back (freshest, not-yet-attempted events) to stay under the ceiling
   * instead of retrying a failing batch forever while the queue balloons. */
  requeueFailed(items) {
    this.eventQueue.unshift(...items);
    if (this.eventQueue.length > MAX_QUEUE_SIZE) {
      this.eventQueue.length = MAX_QUEUE_SIZE;
    }
  }

  setUserId(userId) {
    this.userId = userId;
  }

  // ---- Core tracking method ----

  async track(eventName, properties = {}) {
    if (!hasGivenConsent('analytics')) return;

    const event = {
      sessionId: this.sessionId,
      userId: this.userId,
      eventType: 'analytics',
      eventName,
      properties: {
        ...properties,
        url: window.location.pathname,
        platform: PLATFORM,
      },
      timestamp: new Date().toISOString(),
      environment: this.isOnline ? 'online' : 'offline',
    };

    try {
      await screenwriterDB.init();
      const db = screenwriterDB.db;
      const tx = db.transaction([STORES.ANALYTICS_EVENTS], 'readwrite');
      const store = tx.objectStore(STORES.ANALYTICS_EVENTS);
      const eventId = crypto.randomUUID();

      await store.add({
        id: eventId,
        ...event,
        synced: false,
        retryCount: 0,
        createdAt: new Date(),
      });
      await tx.complete;

      this.enqueue({ type: 'analytics', data: { ...event, id: eventId } });

      if (this.eventQueue.length >= BATCH_SIZE) {
        this.flush();
      }
    } catch {
      // Silently drop — analytics should never break the app
    }
  }

  // ---- Convenience methods for standard SaaS events ----

  /** Track a page view */
  pageView(pageName, properties = {}) {
    return this.track('page_view', { page: pageName, ...properties });
  }

  /** Track a file save event (uses dedicated save type for server-side SaveEvent table) */
  async trackSave(saveData) {
    if (!hasGivenConsent('analytics')) return;

    const {
      fileId, fileName, saveType = 'manual', changesMade = 0,
      duration = 0, success = true, error = null,
    } = saveData;

    const saveEvent = {
      userId: this.userId,
      fileId,
      fileName,
      saveType,
      changesMade,
      duration,
      success,
      error,
      timestamp: new Date().toISOString(),
    };

    try {
      await screenwriterDB.init();
      const db = screenwriterDB.db;
      const tx = db.transaction([STORES.SAVE_EVENTS], 'readwrite');
      const store = tx.objectStore(STORES.SAVE_EVENTS);
      const eventId = crypto.randomUUID();

      await store.add({
        id: eventId, ...saveEvent, synced: false, retryCount: 0, createdAt: new Date(),
      });
      await tx.complete;

      this.enqueue({ type: 'save', data: { ...saveEvent, id: eventId } });

      if (this.eventQueue.length >= BATCH_SIZE) this.flush();
    } catch {
      // Silent
    }
  }

  /** Track a feature usage event */
  featureUsed(featureName, properties = {}) {
    return this.track('feature_used', { feature: featureName, ...properties });
  }

  /** Track an error occurrence */
  trackError(errorName, details = {}) {
    return this.track('error', { errorName, ...details });
  }

  /**
   * Reload un-synced events from IndexedDB into the in-memory queue.
   * This ensures events queued while offline survive page reloads.
   */
  async rehydrateQueue() {
    try {
      await screenwriterDB.init();
      const db = screenwriterDB.db;

      const loadUnsynced = async (storeName, eventType) => {
        const tx = db.transaction([storeName], 'readonly');
        const store = tx.objectStore(storeName);
        const all = await store.getAll();
        return (all || [])
          .filter(e => !e.synced)
          .map(e => ({ type: eventType, data: e, dbId: e.id }));
      };

      const analytics = await loadUnsynced(STORES.ANALYTICS_EVENTS, 'analytics');
      const saves = await loadUnsynced(STORES.SAVE_EVENTS, 'save');

      // Deduplicate against events already in the queue
      const existingIds = new Set(this.eventQueue.map(e => e.data?.id || e.dbId));
      const newEvents = [...analytics, ...saves].filter(e => !existingIds.has(e.dbId));

      if (newEvents.length > 0) {
        this.enqueue(...newEvents);
      }
    } catch {
      // Silent — rehydration is best-effort
    }
  }

  // ---- Flush / sync logic ----

  startPeriodicFlush() {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL);
  }

  async flush() {
    if (!hasGivenConsent('analytics')) return;
    if (!this.isOnline || this.isSyncing || this.eventQueue.length === 0) return;

    const eventsToSync = this.eventQueue.splice(0, BATCH_SIZE);
    this.isSyncing = true;

    try {
      const fetchOptions = {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: eventsToSync }),
      };

      const response = await fetch(`${API_BASE_URL}/api/analytics/batch`, fetchOptions);

      if (response.status === 404 || response.status === 405) {
        // Analytics endpoint not available — mark synced, stop retrying
        await this.markEventsSynced(eventsToSync);
        return;
      }

      if (response.ok) {
        await this.markEventsSynced(eventsToSync);
      } else {
        // Put back for retry
        this.requeueFailed(eventsToSync);
      }
    } catch {
      this.requeueFailed(eventsToSync);
    } finally {
      this.isSyncing = false;
    }
  }

  flushSync() {
    if (!hasGivenConsent('analytics')) return;
    if (!this.isOnline || this.eventQueue.length === 0) return;

    // Best-effort flush remaining events via sendBeacon (fires on beforeunload)
    const eventsToSync = this.eventQueue.splice(0, BATCH_SIZE);
    const blob = new Blob([JSON.stringify({ events: eventsToSync })], { type: 'application/json' });
    navigator.sendBeacon(`${API_BASE_URL}/api/analytics/batch`, blob);
  }

  async markEventsSynced(events) {
    try {
      await screenwriterDB.init();
      const db = screenwriterDB.db;

      const markInStore = async (storeName, batch) => {
        if (batch.length === 0) return;
        const tx = db.transaction([storeName], 'readwrite');
        const store = tx.objectStore(storeName);
        for (const event of batch) {
          const eventId = event.dbId || event.data?.id;
          if (eventId) {
            const record = await store.get(eventId);
            if (record) {
              await store.put({ ...record, id: record.id || eventId, synced: true, syncedAt: new Date() });
            }
          }
        }
        await tx.complete;
      };

      await markInStore(STORES.ANALYTICS_EVENTS, events.filter(e => e.type === 'analytics'));
      await markInStore(STORES.SAVE_EVENTS, events.filter(e => e.type === 'save'));
    } catch {
      // Silent — analytics bookkeeping should never throw
    }
  }

  destroy() {
    if (this.flushTimer) clearInterval(this.flushTimer);
  }
}

// Singleton
let instance = null;
export function getAnalyticsService() {
  if (!instance) instance = new AnalyticsService();
  return instance;
}

/**
 * Public API — import { analytics } from './analyticsService'
 *
 * Standard SaaS events to use:
 *   analytics.track('signup_completed')
 *   analytics.pageView('/editor')
 *   analytics.featureUsed('ai_chat', { mode: 'ask' })
 *   analytics.featureUsed('export', { format: 'pdf' })
 *   analytics.featureUsed('collaboration_invite')
 *   analytics.trackSave({ fileId, fileName, saveType: 'auto' })
 *   analytics.trackError('api_failure', { endpoint: '/api/foo', status: 500 })
 */
export const analytics = {
  track: (eventName, properties = {}) => getAnalyticsService().track(eventName, properties),
  pageView: (pageName, properties = {}) => getAnalyticsService().pageView(pageName, properties),
  featureUsed: (featureName, properties = {}) => getAnalyticsService().featureUsed(featureName, properties),
  trackSave: (saveData) => getAnalyticsService().trackSave(saveData),
  trackError: (errorName, details = {}) => getAnalyticsService().trackError(errorName, details),
  setUserId: (userId) => getAnalyticsService().setUserId(userId),

};

export default analytics;
